import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

import { useNavigate, useLocation } from 'react-router-dom';
import './CommonStyles.css';
import './AddSellStyles.css';
import './ProductSearchStyles.css';
import { ChevronRight, User, PlusCircle } from 'lucide-react';
import apiService from '../../services/api';
import '../../addproduct.css';
import WhatsAppIntegration from '../WhatsAppNotification/WhatsAppIntegration';
import whatsappService from '../../services/whatsapp';
import invoiceGenerator from '../../services/invoiceGenerator';
import { usePermissionContext } from '../../contexts/PermissionContext';
import AddProduct from '../../AddProduct';
import AddSupplier from './AddSupplier';
import dateUtils from '../../utils/dateUtils';


const initialFormData = {
  customer: '',
  customerId: '',
  billingAddress: '',
  shippingAddress: '',
  shippingCity: '',
  shippingState: '',
  shippingCountry: '',
  shippingZipCode: '',
  payTerm: '',
  payTermPeriod: 'Months',
  commissionAgent: '',
  saleDate: dateUtils.getChicagoISOString().split('T')[0],
  status: 'Draft',
  invoiceScheme: '',
  invoiceNo: '',
  locationId: '',
  products: [],
  productName: '',
  quantity: '',
  unitPrice: '',
  discount: '',
  tax: '',
  discountType: 'Percentage',
  discountAmount: '0.00',
  redeemedAmount: '0.00',
  orderTax: 'None',
  orderTaxAmount: '0.00',
  shippingCharges: '0.00',
  roundOff: '0',
  totalPayable: '0.00',
  productAmount: '0.00',
  payments: [],
  amount: '0.00',
  paidOn: dateUtils.formatDateTime(new Date()),
  paymentMethod: 'Cash',
  paymentAccount: 'None',
  paymentNote: '',
  sellNote: '',
  additionalNotes: '',
  staffNote: '',
  shippingDetails: '',
  deliveredTo: '',
  deliveryPerson: '',
  shippingStatus: '',
  serviceStaff: '',
  checkNo: '',
  checkExpiryDate: '',
  bankSubmissionDate: '',
  checkImage: null,
  cardNumber: '',
  cardHolderName: '',
  cardExpiry: '',
  cardCvv: '',
  serviceCustomField1: '',
  serviceCustomField2: '',
  serviceCustomField3: '',
  serviceCustomField4: '',
  serviceCustomField5: '',
  serviceCustomField6: '',
  additionalExpenseKey1: '',
  additionalExpenseValue1: '',
  additionalExpenseKey2: '',
  additionalExpenseValue2: '',
  additionalExpenseKey3: '',
  additionalExpenseValue3: '',
  additionalExpenseKey4: '',
  additionalExpenseValue4: '',
  additionalExpenses: [
    { id: 1, name: '', amount: '' },
    { id: 2, name: '', amount: '' },
    { id: 3, name: '', amount: '' },
    { id: 4, name: '', amount: '' }
  ]
};

const AddDraft = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = usePermissionContext();
  const editSale = location.state?.editSale || null;
  const isEditMode = !!editSale;
  // Customer states
  const [customers, setCustomers] = useState([]);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
  const [customerError, setCustomerError] = useState('');

  // Tax states
  const [taxRates, setTaxRates] = useState([
    { id: 0, name: 'None', amount: 0, type: 'percentage' },
    { id: 1, name: 'Beer Tax', amount: 0, type: 'percentage' },
    { id: 2, name: 'Food', amount: 0, type: 'percentage' },
    { id: 3, name: 'Wine And Liquor 10.5%', amount: 10.5, type: 'percentage' },
    { id: 4, name: 'No Tax', amount: 0, type: 'percentage' },
    { id: 5, name: 'Sales Tax', amount: 0, type: 'percentage' },
    { id: 6, name: 'Cigarettes', amount: 0, type: 'percentage' }
  ]);
  const [isLoadingTaxRates, setIsLoadingTaxRates] = useState(false);
  const [taxError, setTaxError] = useState('');

  // Payment methods states
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] = useState(false);
  const [paymentMethodsError, setPaymentMethodsError] = useState('');

  const [formData, setFormData] = useState({
    ...initialFormData,
    saleDate: dateUtils.getChicagoISOString().split('T')[0]
  });

  // File input ref for check image
  const checkImageInputRef = React.useRef(null);

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Customer dropdown states
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [filteredCustomers, setFilteredCustomers] = useState([]);

  // Product search states
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [productError, setProductError] = useState('');
  const [displayLimit, setDisplayLimit] = useState(50);

  // Modal states
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [showAddProductModal, setShowAddProductModal] = useState(false);

  const [advanceBalance, setAdvanceBalance] = useState('0.00');

  // Derived state for balance and change return
  const totalPaid = formData.payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  const totalPayableVal = parseFloat(formData.totalPayable) || 0;
  let balanceVal = totalPayableVal - totalPaid;
  let changeReturnVal = 0;

  if (balanceVal < 0) {
    changeReturnVal = Math.abs(balanceVal);
    balanceVal = 0;
  }

  const balance = balanceVal.toFixed(2);
  const changeReturn = changeReturnVal.toFixed(2);

  // State for additional expenses visibility
  const [showAdditionalExpenses, setShowAdditionalExpenses] = useState(false);

  // State for calculation breakdown expansion
  const [isCalculationExpanded, setIsCalculationExpanded] = useState(false);
  const [isExpensesExpanded, setIsExpensesExpanded] = useState(false);

  // Payment accounts state
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [isLoadingPaymentAccounts, setIsLoadingPaymentAccounts] = useState(false);
  const [paymentAccountsError, setPaymentAccountsError] = useState('');

  // WhatsApp notification state
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [lastSaleData, setLastSaleData] = useState(null);

  // Business locations state
  const [businessLocations, setBusinessLocations] = useState([{ id: 1, name: 'Lefty Liquor' }]);

  // Invoice Schemes state
  const [invoiceSchemes, setInvoiceSchemes] = useState([]);
  const [isLoadingInvoiceSchemes, setIsLoadingInvoiceSchemes] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    // Required field validations
    if (!formData.customer.trim()) {
      newErrors.customer = 'Customer is required';
    }

    if (!formData.locationId) {
      newErrors.locationId = 'Business location is required';
    }

    if (!formData.saleDate.trim()) {
      newErrors.saleDate = 'Sale date is required';
    }

    if (!formData.status) {
      newErrors.status = 'Status is required';
    }

    if (!formData.totalPayable || parseFloat(formData.totalPayable) <= 0) {
      newErrors.amount = 'Amount must be greater than 0';
    }

    // Payment fields are now OPTIONAL - removed mandatory validation
    // Users can create sales without adding payment

    // Validate total payable
    if (parseFloat(formData.totalPayable) <= 0) {
      newErrors.totalPayable = 'Total payable must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Load business locations silently — needed to get the correct location_id for saving
  const loadBusinessLocation = async () => {
    try {
      const response = await apiService.getBusinessLocations();
      const locations = response.data || (Array.isArray(response) ? response : []);
      
      if (locations.length > 0) {
        setBusinessLocations(locations);
        
        // Auto-select logged-in user's location if not in edit mode
        if (!isEditMode && !formData.locationId) {
          try {
            const userDetails = await apiService.getUserDetails();
            const userLocationId = userDetails.data?.location_id || userDetails.location_id;
            
            if (userLocationId) {
              const matchedLocation = locations.find(loc => String(loc.id) === String(userLocationId));
              if (matchedLocation) {
                console.log('[AddDraft] Auto-selecting user location:', userLocationId);
                setFormData(prev => ({ ...prev, locationId: String(userLocationId) }));
              } else {
                console.log('[AddDraft] User location ID', userLocationId, 'not found in locations list, defaulting to first.');
                setFormData(prev => ({ ...prev, locationId: String(locations[0].id) }));
              }
            } else {
              setFormData(prev => ({ ...prev, locationId: String(locations[0].id) }));
            }
          } catch (userErr) {
            console.error('[AddDraft] Error fetching user details for location:', userErr);
            setFormData(prev => ({ ...prev, locationId: String(locations[0].id) }));
          }
        }
        
        console.log('[AddDraft] Locations loaded, total:', locations.length);
      }
    } catch (error) {
      console.error('[AddDraft] Could not load location, using default ID=1:', error);
    }
  };

  // Load products, payment accounts, customers, tax rates, and locations on component mount
  useEffect(() => {
    if (apiService.isTokenValid()) {
      loadProducts();
      loadPaymentAccounts();
      loadCustomers();
      loadTaxRates();
      loadBusinessLocation();
      loadPaymentMethods();
      loadInvoiceSchemes();
    } else {
      console.log('User not authenticated, skipping data loading');
    }
  }, []);

  // Pre-fill form when editing an existing sale
  useEffect(() => {
    if (editSale) {
      const apiData = editSale.apiData || editSale;
      console.log('Edit mode - loading sale data:', apiData);

      // Build products array from sell_lines
      const editProducts = (apiData.sell_lines || editSale.products || []).map(line => {
        console.log('=== LOADING SELL LINE FOR EDIT ===');
        console.log('Raw sell_line data:', line);
        console.log('product_id:', line.product_id);
        console.log('variation_id:', line.variation_id);
        console.log('variation_id type:', typeof line.variation_id);

        const productData = {
          id: line.product_id || line.id,
          variation_id: line.variation_id, // Use exact variation_id from API - DO NOT fallback to product_id
          name: line.product?.name || line.variations?.name || line.product_name || line.name || `Product #${line.product_id}`,
          sku: line.product?.sku || line.variations?.sub_sku || line.sku || '',
          quantity: parseFloat(line.quantity) || 1,
          unitPrice: parseFloat(line.unit_price || line.unitPrice || 0),
          purchasePrice: parseFloat(line.purchase_price || 0),
          discount: parseFloat(line.line_discount_amount || line.discount || 0),
          discountType: line.line_discount_type ? (line.line_discount_type === 'percentage' ? 'Percentage' : 'Fixed') : 'Percentage',
          tax: line.tax?.name || line.tax_name || line.tax || 'None',
          priceIncTax: parseFloat(line.unit_price_inc_tax || line.priceIncTax || line.unit_price || 0),
          subtotal: parseFloat(line.unit_price_inc_tax || line.priceIncTax || line.unit_price || 0) * (parseFloat(line.quantity) || 1)
        };

        console.log('Mapped product data:', productData);
        console.log('=== END LOADING SELL LINE ===');

        return productData;
      });

      const productAmount = editProducts.reduce((sum, p) => sum + (p.subtotal || 0), 0);

      setFormData(prev => ({
        ...prev,
        customer: apiData.contact?.name || editSale.customer || '',
        customerId: apiData.contact_id || editSale.customerId || '',
        billingAddress: apiData.contact?.address_line_1 || editSale.billingAddress || '',
        shippingAddress: apiData.shipping_address || editSale.shippingAddress || '',
        saleDate: apiData.transaction_date ? dateUtils.getChicagoISOString(apiData.transaction_date).split('T')[0] : (editSale.saleDate || dateUtils.getChicagoISOString().split('T')[0]),
        status: apiData.sub_status === 'quotation' ? 'Quotation' :
                apiData.sub_status === 'proforma' ? 'Proforma' :
                apiData.status === 'draft' ? 'Draft' :
                apiData.status === 'final' ? 'Final' : 'Draft',
        invoiceNo: apiData.invoice_no || editSale.invoiceNo || '',
        locationId: apiData.location_id || editSale.locationId || '1',
        commissionAgent: apiData.commission_agent || editSale.commissionAgent || '',
        serviceStaff: apiData.res_waiter_id || editSale.serviceStaff || '',
        payTerm: apiData.pay_term_number || editSale.payTerm || '',
        payTermPeriod: apiData.pay_term_type || editSale.payTermPeriod || 'Months',
        products: editProducts,
        productAmount: productAmount.toFixed(2),
        discountType: apiData.discount_type === 'percentage' ? 'Percentage' : (apiData.discount_type || 'Percentage'),
        discountAmount: apiData.discount_amount || editSale.discountAmount || '0.00',
        orderTax: apiData.tax?.name || editSale.orderTax || 'None',
        orderTaxAmount: apiData.tax_amount || editSale.orderTaxAmount || '0.00',
        shippingCharges: apiData.shipping_charges || editSale.shippingCharges || '0.00',
        shippingDetails: apiData.shipping_details || editSale.shippingDetails || '',
        shippingStatus: apiData.shipping_status || editSale.shippingStatus || '',
        deliveredTo: apiData.delivered_to || editSale.deliveredTo || '',
        deliveryPerson: apiData.delivery_person || editSale.deliveryPerson || '',
        sellNote: apiData.additional_notes || editSale.sellNote || '',
        staffNote: apiData.staff_note || editSale.staffNote || '',
        roundOff: apiData.round_off_amount || editSale.roundOff || '0',
        totalPayable: apiData.final_total || editSale.totalPayable || '0.00',

        // Service Custom Fields
        serviceCustomField1: apiData.service_custom_field_1 || '',
        serviceCustomField2: apiData.service_custom_field_2 || '',
        serviceCustomField3: apiData.service_custom_field_3 || '',
        serviceCustomField4: apiData.service_custom_field_4 || '',
        serviceCustomField5: apiData.service_custom_field_5 || '',
        serviceCustomField6: apiData.service_custom_field_6 || '',

        // Additional Expenses
        additionalExpenseKey1: apiData.additional_expense_key_1 || '',
        additionalExpenseValue1: apiData.additional_expense_value_1 || '',
        additionalExpenseKey2: apiData.additional_expense_key_2 || '',
        additionalExpenseValue2: apiData.additional_expense_value_2 || '',
        additionalExpenseKey3: apiData.additional_expense_key_3 || '',
        additionalExpenseValue3: apiData.additional_expense_value_3 || '',
        additionalExpenseKey4: apiData.additional_expense_key_4 || '',
        additionalExpenseValue4: apiData.additional_expense_value_4 || '',

        // Payment Lines - map from API payment_lines
        payments: (apiData.payment_lines || []).map(payment => ({
          id: payment.id,
          amount: parseFloat(payment.amount || 0).toFixed(2),
          paidOn: payment.paid_on || new Date().toISOString().slice(0, 16),
          paymentMethod: payment.method ? payment.method.charAt(0).toUpperCase() + payment.method.slice(1) : 'Cash',
          paymentAccount: payment.account_id || 'None',
          account_id: payment.account_id,
          paymentNote: payment.note || '',
          cardNumber: payment.card_number || '',
          cardHolderName: payment.card_holder_name || '',
          cardExpiry: payment.card_month && payment.card_year ? `${payment.card_month}/${payment.card_year}` : '',
          cardCvv: payment.card_security || '',
          checkNumber: payment.cheque_number || '',
          checkExpiryDate: payment.cheque_expiry_date || '',
          bankSubmissionDate: payment.bank_submission_date || ''
        }))
      }));

      // Set customer search term for display
      setCustomerSearchTerm(apiData.contact?.name || editSale.customer || '');
    }
  }, [editSale]);


  // Recalculate totals when products or pricing changes
  useEffect(() => {
    calculateTotals(formData);
  }, [formData.productAmount, formData.discountAmount, formData.orderTaxAmount, formData.shippingCharges, formData.roundOff, formData.additionalExpenses, formData.products]);

  // Recalculate product subtotal when products change
  useEffect(() => {
    calculateOrderTotals();
  }, [formData.products]);

  // Auto-update amount field when total payable or payments change
  useEffect(() => {
    const paid = formData.payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
    const total = parseFloat(formData.totalPayable) || 0;
    const remaining = Math.max(0, total - paid);

    setFormData(prev => {
      // Only update if amount matches the OLD balance or is 0? 
      // Actually, better to just set it to remaining balance to guide the user.
      // But we must avoid overwriting user typing if they are in the middle of typing?
      // Since this effect runs on totalPayable/payments change, it shouldn't interfere with typing in 'amount' field (which updates formData.amount but not totalPayable/payments).
      return {
        ...prev,
        amount: remaining.toFixed(2)
      };
    });
  }, [formData.totalPayable, formData.payments]);

  const loadPaymentAccounts = async () => {
    try {
      setIsLoadingPaymentAccounts(true);
      setPaymentAccountsError('');
      const accountsData = await apiService.getPaymentAccounts();
      const accounts = accountsData.data || accountsData || [];
      console.log('Payment accounts from API (already deduplicated):', accounts);
      setPaymentAccounts(accounts);
    } catch (error) {
      setPaymentAccountsError('Failed to load payment accounts');
      console.error('Error loading payment accounts:', error);
    } finally {
      setIsLoadingPaymentAccounts(false);
    }
  };

  const loadCustomers = async () => {
    try {
      setIsLoadingCustomers(true);
      setCustomerError('');
      const customersData = await apiService.getContacts();

      // Transform API data to match the expected format
      const transformedCustomers = (customersData.data || customersData || []).map(contact => ({
        id: contact.id,
        name: contact.name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Unknown',
        phone: contact.mobile || contact.contact_no || contact.phone || '',
        email: contact.email || '',
        address: contact.address || contact.billing_address || contact.shipping_address || '',
        balance: contact.balance,
        advance_balance: contact.advance_balance,
        due_amount: contact.due_amount
      }));

      // Add default "Walk-In Customer" if not present
      if (!transformedCustomers.find(c => c.name === 'Walk-In Customer')) {
        transformedCustomers.unshift({ id: 0, name: 'Walk-In Customer', phone: '', email: '', address: '' });
      }

      setCustomers(transformedCustomers);
      setFilteredCustomers(transformedCustomers);
    } catch (error) {
      setCustomerError('Failed to load customers');
      console.error('Error loading customers:', error);

      // Fallback to default customer data
      const fallbackCustomers = [{ id: 0, name: 'Walk-In Customer', phone: '', email: '', address: '' }];
      setCustomers(fallbackCustomers);
      setFilteredCustomers(fallbackCustomers);
    } finally {
      setIsLoadingCustomers(false);
    }
  };

  const loadTaxRates = async () => {
    try {
      setIsLoadingTaxRates(true);
      setTaxError('');
      const taxData = await apiService.getTaxRates();

      // Transform API data to match the expected format
      const transformedTaxRates = (taxData.data || taxData || []).map(tax => ({
        id: tax.id,
        name: tax.name || tax.description || tax.tax_name || `Tax ${tax.id}`,
        amount: tax.amount || tax.rate || tax.percentage || 0,
        type: tax.type || 'percentage' // default to percentage
      }));

      // Add default "None" option if not present
      if (!transformedTaxRates.find(t => t.name === 'None')) {
        transformedTaxRates.unshift({ id: 0, name: 'None', amount: 0, type: 'percentage' });
      }

      setTaxRates(transformedTaxRates);
    } catch (error) {
      setTaxError('Failed to load tax rates');
      console.error('Error loading tax rates:', error);
    } finally {
      setIsLoadingTaxRates(false);
    }
  };

  const loadPaymentMethods = async () => {
    try {
      setIsLoadingPaymentMethods(true);
      setPaymentMethodsError('');
      const paymentMethodsData = await apiService.getPaymentMethods();

      // Transform API data to match the expected format
      // Handle the response format: {"cash": "Cash", "card": "Card", "check": "Check", ...}
      const transformedPaymentMethods = Object.entries(paymentMethodsData || {}).map(([key, value]) => ({
        id: key,
        name: value,
        type: key
      }));

      setPaymentMethods(transformedPaymentMethods);
    } catch (error) {
      setPaymentMethodsError('Failed to load payment methods');
      console.error('Error loading payment methods:', error);

      // Fallback to default payment options
      const fallbackPaymentMethods = [
        { id: 'cash', name: 'Cash', type: 'cash' },
        { id: 'card', name: 'Card', type: 'card' },
        { id: 'bank_transfer', name: 'Bank Transfer', type: 'bank_transfer' }
      ];
      setPaymentMethods(fallbackPaymentMethods);
    } finally {
      setIsLoadingPaymentMethods(false);
    }
  };

  const loadInvoiceSchemes = async () => {
    try {
      setIsLoadingInvoiceSchemes(true);
      const schemesData = await apiService.getInvoiceSchemes();
      // The API returns nested structure: data.invoice_schemes.details
      // api.js returns response.json(), so we access data.invoice_schemes
      const schemes = schemesData.data?.invoice_schemes?.details || [];
      const defaultScheme = schemesData.data?.invoice_schemes?.default;

      setInvoiceSchemes(schemes);

      // Set default scheme if not editing and no scheme selected
      if (!isEditMode && !formData.invoiceScheme && schemes.length > 0) {
        if (defaultScheme) {
          setFormData(prev => ({ ...prev, invoiceScheme: defaultScheme.id }));
        } else {
          // Fallback to first scheme if no default specified
          setFormData(prev => ({ ...prev, invoiceScheme: schemes[0].id }));
        }
      }
    } catch (error) {
      console.error('Error loading invoice schemes:', error);
    } finally {
      setIsLoadingInvoiceSchemes(false);
    }
  };

  const loadProducts = async () => {
    try {
      setIsLoadingProducts(true);
      setProductError('');
      const productsData = await apiService.getProducts({ per_page: 250 });
      setProducts(productsData.data || productsData || []);
      setFilteredProducts(productsData.data || productsData || []);
    } catch (error) {
      setProductError('Failed to load products');
      console.error('Error loading products:', error);
    } finally {
      setIsLoadingProducts(false);
    }
  };
  
  const handleProductSearch = async (searchTerm) => {
    setProductSearchTerm(searchTerm);
    setDisplayLimit(250); // Reset display limit for new searches

    try {
      setIsLoadingProducts(true);
      setProductError('');

      const productsData = await apiService.getProducts({ search: searchTerm, per_page: 250 });
      let productList = productsData.data || productsData || [];

      // Only apply frontend filtering if we have a search term and API doesn't filter
      if (searchTerm.trim() && productList.length > 0) {
        const searchLower = searchTerm.toLowerCase().trim();
        productList = productList.filter(product => {
          const name = (product.product || product.name || product.product_name || product.title || product.description || '').toLowerCase();
          const sku = (product.sku || product.code || '').toLowerCase();
          const barcode = (product.barcode || '').toLowerCase();

          // Check for any match (name, sku, or barcode)
          const matches = name.includes(searchLower) ||
            sku.includes(searchLower) ||
            barcode.includes(searchLower);

          return matches;
        });

        // Sort by relevance: exact matches first, then name matches
        productList.sort((a, b) => {
          const aSku = (a.sku || a.code || '').toLowerCase();
          const bSku = (b.sku || b.code || '').toLowerCase();
          const aBarcode = (a.barcode || '').toLowerCase();
          const bBarcode = (b.barcode || '').toLowerCase();
          const aName = (a.product || a.name || a.product_name || a.title || a.description || '').toLowerCase();
          const bName = (b.product || b.name || b.product_name || b.title || b.description || '').toLowerCase();

          // Exact SKU/barcode matches first
          const aExact = aSku === searchLower || aBarcode === searchLower;
          const bExact = bSku === searchLower || bBarcode === searchLower;

          if (aExact && !bExact) return -1;
          if (!aExact && bExact) return 1;

          // Then by name starts with
          const aStarts = aName.startsWith(searchLower);
          const bStarts = bName.startsWith(searchLower);

          if (aStarts && !bStarts) return -1;
          if (!aStarts && bStarts) return 1;

          // Then alphabetical
          return aName.localeCompare(bName);
        });
      }

      setProducts(productList);
      setFilteredProducts(productList);
      setShowProductDropdown(true);
    } catch (error) {
      setProductError('Failed to search products');
      console.error('Error searching products:', error);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const calculateProductTax = (unitPrice, taxName) => {
    const selectedTax = taxRates.find(tax => tax.name === taxName);
    if (selectedTax && selectedTax.amount > 0) {
      if (selectedTax.type === 'percentage') {
        return (unitPrice * selectedTax.amount) / 100;
      } else if (selectedTax.type === 'fixed') {
        return selectedTax.amount;
      }
    }
    return 0;
  };

  const handleProductSelect = (product) => {
    console.log('Product selected:', product);

    // Add product to the form
    // Extract Selling Price (Unit Price)
    const unitPrice = parseFloat(
      product.unit_price ||
      product.price ||
      product.sell_price_inc_tax ||
      product.product_variations?.[0]?.variations?.[0]?.sell_price_inc_tax ||
      product.product_variations?.[0]?.variations?.[0]?.default_sell_price ||
      0
    );

    const productName = product.product || product.name || product.product_name || product.title || product.description || 'Unknown Product';
    const productTaxName = product.tax || 'None';

    // Calculate tax for this product
    const productTaxAmount = calculateProductTax(unitPrice, productTaxName);
    const priceIncTax = unitPrice + productTaxAmount;

    // Extract purchase price (Previous Unit Price)
    const purchasePrice = parseFloat(
      product.purchase_price ||
      product.default_purchase_price ||
      product.cost ||
      product.product_variations?.[0]?.variations?.[0]?.default_purchase_price ||
      0
    );

    // Extract available stock for validation
    const stockAvailable = parseFloat(
      product.qty_available ||
      product.current_stock ||
      product.stock ||
      product.total_qty_available ||
      (product.product_variations?.[0]?.variations?.[0]?.variation_location_details?.reduce((sum, vld) => sum + (parseFloat(vld.qty_available) || 0), 0)) ||
      (product.variation_location_details?.reduce((sum, vld) => sum + (parseFloat(vld.qty_available) || 0), 0)) ||
      0
    );

    const newProduct = {
      id: product.id || Date.now(),
      variation_id: product.variation_id || (product.product_variations?.[0]?.variations?.[0]?.id) || product.id, // Store variation_id for API
      name: productName,
      sku: product.sku || product.code || '',
      quantity: 1,
      unitPrice: unitPrice,
      purchasePrice: purchasePrice, // Store for display
      discount: 0,
      discountType: 'percentage', // Add discount type field
      tax: productTaxName,
      priceIncTax: priceIncTax,
      subtotal: priceIncTax,
      stock_available: stockAvailable
    };

    setFormData(prev => {
      const updatedProducts = [...prev.products, newProduct];
      const subtotal = updatedProducts.reduce((sum, product) =>
        sum + (parseFloat(product.subtotal) || 0), 0
      );

      console.log('Adding product - new subtotal:', subtotal);

      return {
        ...prev,
        products: updatedProducts,
        productName: '',
        productAmount: subtotal.toFixed(2)
      };
    });

    setProductSearchTerm('');
    setShowProductDropdown(false);

    setProductSearchTerm('');
    setShowProductDropdown(false);
  };

  const handleProductInputFocus = () => {
    if (products.length > 0) {
      setFilteredProducts(products);
      setShowProductDropdown(true);
    } else {
      loadProducts();
    }
  };

  const handleProductInputBlur = () => {
    // Delay closing dropdown to allow click on dropdown items
    setTimeout(() => {
      setShowProductDropdown(false);
    }, 150);
  };

  const removeProduct = (productId) => {
    setFormData(prev => {
      const updatedProducts = prev.products.filter(product => product.id !== productId);
      const subtotal = updatedProducts.reduce((sum, product) =>
        sum + (parseFloat(product.subtotal) || 0), 0
      );

      console.log('Removing product - new subtotal:', subtotal);

      return {
        ...prev,
        products: updatedProducts,
        productAmount: subtotal.toFixed(2)
      };
    });
  };

  const calculateOrderTotals = () => {
    console.log('calculateOrderTotals - products:', formData.products);
    const subtotal = formData.products.reduce((sum, product) =>
      sum + (parseFloat(product.subtotal) || 0), 0
    );

    console.log('calculateOrderTotals - calculated subtotal:', subtotal);

    setFormData(prev => {
      const updated = {
        ...prev,
        productAmount: subtotal.toFixed(2)
      };
      console.log('Setting productAmount to:', updated.productAmount);
      calculateTotals(updated);
      return updated;
    });
  };

  const updateProductQuantity = (productId, newQuantity) => {
    const quantity = parseInt(newQuantity) || 1;
    setFormData(prev => {
      const updatedProducts = prev.products.map(product => {
        if (product.id === productId) {
          // Calculate tax for this product
          const productTaxAmount = calculateProductTax(parseFloat(product.unitPrice) || 0, product.tax);
          const priceIncTax = (parseFloat(product.unitPrice) || 0) + productTaxAmount;

          // Calculate discount based on type
          let discountAmount = parseFloat(product.discount) || 0;
          if (product.discountType === 'Percentage') {
            discountAmount = (priceIncTax * discountAmount) / 100;
          }

          const subtotal = quantity * priceIncTax - discountAmount;
          return {
            ...product,
            quantity,
            priceIncTax: priceIncTax.toFixed(2),
            subtotal: subtotal.toFixed(2)
          };
        }
        return product;
      });

      const subtotal = updatedProducts.reduce((sum, product) =>
        sum + (parseFloat(product.subtotal) || 0), 0
      );

      console.log('Updating quantity - new subtotal:', subtotal);

      return {
        ...prev,
        products: updatedProducts,
        productAmount: subtotal.toFixed(2)
      };
    });

    // Recalculate totals after state updates
    setTimeout(() => {
      calculateTotals(formData);
    }, 50);
  };

  const updateProductTax = (productId, newTaxName) => {
    setFormData(prev => {
      const updatedProducts = prev.products.map(product => {
        if (product.id === productId) {
          // Calculate tax for this product
          const productTaxAmount = calculateProductTax(parseFloat(product.unitPrice) || 0, newTaxName);
          const priceIncTax = (parseFloat(product.unitPrice) || 0) + productTaxAmount;

          // Calculate discount based on type
          let discountAmount = parseFloat(product.discount) || 0;
          if (product.discountType === 'Percentage') {
            discountAmount = (priceIncTax * discountAmount) / 100;
          }

          const subtotal = (product.quantity * priceIncTax) - discountAmount;
          return {
            ...product,
            tax: newTaxName,
            priceIncTax: priceIncTax.toFixed(2),
            subtotal: Math.max(0, subtotal).toFixed(2)
          };
        }
        return product;
      });

      const subtotal = updatedProducts.reduce((sum, product) =>
        sum + (parseFloat(product.subtotal) || 0), 0
      );

      console.log('Updating tax - new subtotal:', subtotal);

      return {
        ...prev,
        products: updatedProducts,
        productAmount: subtotal.toFixed(2)
      };
    });

    // Recalculate totals after state updates
    setTimeout(() => {
      calculateTotals(formData);
    }, 50);
  };

  const updateProductDiscount = (productId, newDiscount) => {
    const discount = parseFloat(newDiscount) || 0;
    setFormData(prev => {
      const updatedProducts = prev.products.map(product => {
        if (product.id === productId) {
          // Calculate tax for this product
          const productTaxAmount = calculateProductTax(parseFloat(product.unitPrice) || 0, product.tax);
          const priceIncTax = (parseFloat(product.unitPrice) || 0) + productTaxAmount;

          // Calculate discount based on type
          let discountAmount = discount;
          if (product.discountType === 'Percentage') {
            discountAmount = (priceIncTax * discount) / 100;
          }

          const subtotal = (product.quantity * priceIncTax) - discountAmount;
          return {
            ...product,
            discount,
            priceIncTax: priceIncTax.toFixed(2),
            subtotal: Math.max(0, subtotal).toFixed(2)
          };
        }
        return product;
      });

      const subtotal = updatedProducts.reduce((sum, product) =>
        sum + (parseFloat(product.subtotal) || 0), 0
      );

      console.log('Updating discount - new subtotal:', subtotal);

      return {
        ...prev,
        products: updatedProducts,
        productAmount: subtotal.toFixed(2)
      };
    });

    // Recalculate totals after state updates
    setTimeout(() => {
      calculateTotals(formData);
    }, 50);
  };

  const updateProductDiscountType = (productId, newDiscountType) => {
    setFormData(prev => {
      const updatedProducts = prev.products.map(product => {
        if (product.id === productId) {
          // Calculate tax for this product
          const productTaxAmount = calculateProductTax(parseFloat(product.unitPrice) || 0, product.tax);
          const priceIncTax = (parseFloat(product.unitPrice) || 0) + productTaxAmount;

          // Recalculate discount based on new type
          let discountAmount = parseFloat(product.discount) || 0;
          if (newDiscountType === 'Percentage') {
            discountAmount = (priceIncTax * discountAmount) / 100;
          }

          const subtotal = (product.quantity * priceIncTax) - discountAmount;
          return {
            ...product,
            discountType: newDiscountType,
            priceIncTax: priceIncTax.toFixed(2),
            subtotal: Math.max(0, subtotal).toFixed(2)
          };
        }
        return product;
      });

      const subtotal = updatedProducts.reduce((sum, product) =>
        sum + (parseFloat(product.subtotal) || 0), 0
      );

      console.log('Updating discount type - new subtotal:', subtotal);

      return {
        ...prev,
        products: updatedProducts,
        productAmount: subtotal.toFixed(2)
      };
    });

    // Recalculate totals after state updates
    setTimeout(() => {
      calculateTotals(formData);
    }, 50);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Handle tax selection - calculate tax amount
    if (name === 'orderTax') {
      handleTaxSelection(value);
    }

    // Auto-calculate totals when relevant fields change
    if (['productAmount', 'discountAmount', 'orderTaxAmount', 'shippingCharges', 'roundOff'].includes(name)) {
      calculateTotals({ ...formData, [name]: value });
    }
  };

  const handleTaxSelection = (selectedTaxName) => {
    const selectedTax = taxRates.find(tax => tax.name === selectedTaxName);
    if (selectedTax) {
      const productAmount = parseFloat(formData.productAmount) || 0;
      const discountAmount = parseFloat(formData.discountAmount) || 0;
      const subtotal = productAmount - discountAmount;

      let taxAmount = 0;
      if (selectedTax.type === 'percentage') {
        taxAmount = (subtotal * selectedTax.amount) / 100;
      } else if (selectedTax.type === 'fixed') {
        taxAmount = selectedTax.amount;
      }

      setFormData(prev => ({
        ...prev,
        orderTaxAmount: taxAmount.toFixed(2)
      }));

      // Recalculate totals with new tax amount
      calculateTotals({ ...formData, orderTax: selectedTaxName, orderTaxAmount: taxAmount.toFixed(2) });
    } else {
      // No tax selected
      setFormData(prev => ({
        ...prev,
        orderTaxAmount: '0.00'
      }));

      // Recalculate totals without tax
      calculateTotals({ ...formData, orderTax: selectedTaxName, orderTaxAmount: '0.00' });
    }
  };

  // Customer search and selection functions with API call
  const handleCustomerSearch = async (searchTerm) => {
    setCustomerSearchTerm(searchTerm);
    setShowCustomerDropdown(true);

    // If empty, show initial customers list
    if (searchTerm.trim() === '') {
      setFilteredCustomers(customers);
      return;
    }

    // Search from API when user types (minimum 2 characters)
    if (searchTerm.trim().length >= 2) {
      try {
        setIsLoadingCustomers(true);
        const customersData = await apiService.getContacts(searchTerm);

        // Transform API data
        const transformedCustomers = (customersData.data || customersData || []).map(contact => ({
          id: contact.id,
          name: contact.name || contact.supplier_business_name || 'Unknown',
          phone: contact.mobile || '',
          email: contact.email || '',
          address: contact.address_line_1 || '',
          balance: contact.balance,
          advance_balance: contact.advance_balance,
          due_amount: contact.due_amount
        }));

        setFilteredCustomers(transformedCustomers);
      } catch (error) {
        console.error('Error searching customers:', error);
        // Fallback to local filtering
        const filtered = customers.filter(customer =>
          customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          customer.phone.includes(searchTerm) ||
          customer.email.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredCustomers(filtered);
      } finally {
        setIsLoadingCustomers(false);
      }
    } else {
      // For 1 character, filter locally
      const filtered = customers.filter(customer =>
        customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.phone.includes(searchTerm) ||
        customer.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCustomers(filtered);
    }
  };

  const handleCustomerSelect = (customer) => {
    setFormData(prev => ({
      ...prev,
      customer: customer.name,
      customerId: customer.id,
      billingAddress: customer.address,
      shippingAddress: customer.address
    }));
    setCustomerSearchTerm(customer.name);
    setShowCustomerDropdown(false);

    // Set advance balance from customer data (now available directly)
    console.log('Customer selected:', customer);
    console.log('Customer balance field:', customer.balance);
    console.log('Customer advance_balance field:', customer.advance_balance);
    console.log('Customer due_amount field:', customer.due_amount);

    const customerBalance = parseFloat(customer.balance) || parseFloat(customer.advance_balance) || parseFloat(customer.due_amount) || 0;
    console.log('Final customer balance:', customerBalance);
    setAdvanceBalance(customerBalance.toString());

    // Clear customer error
    if (errors.customer) {
      setErrors(prev => ({ ...prev, customer: '' }));
    }
  };

  const handleCustomerInputFocus = () => {
    setCustomerSearchTerm(formData.customer);
    setFilteredCustomers(customers);
    setShowCustomerDropdown(true);
  };

  const handleCustomerInputBlur = () => {
    // Delay closing dropdown to allow click on dropdown items
    setTimeout(() => {
      setShowCustomerDropdown(false);
    }, 200);
  };

  const handleAddAdditionalExpenses = () => {
    setShowAdditionalExpenses(true);
  };

  const handleExpenseChange = (index, field, value) => {
    const newExpenses = [...formData.additionalExpenses];
    newExpenses[index][field] = value;
    const updatedFormData = {
      ...formData,
      additionalExpenses: newExpenses
    };
    setFormData(updatedFormData);

    // Trigger total calculation when expense amount changes
    if (field === 'amount') {
      calculateTotals(updatedFormData);
    }
  };

  const handleAddPaymentEntry = () => {
    console.log('=== ADDING PAYMENT ENTRY ===');
    console.log('Current formData.payments before adding:', formData.payments);
    console.log('Amount to add:', formData.amount);
    console.log('Payment method:', formData.paymentMethod);

    // Find account name for display
    let accountName = 'None';
    if (formData.paymentAccount) {
      const selectedAccount = paymentAccounts.find(acc => acc.id == formData.paymentAccount);
      if (selectedAccount) {
        accountName = selectedAccount.name || selectedAccount.account_name || selectedAccount.display_name;
      }
    }

    const newPayment = {
      id: Date.now(),
      amount: formData.amount,
      paidOn: formData.paidOn,
      paymentMethod: formData.paymentMethod,
      paymentAccount: accountName, // For display
      account_id: formData.paymentAccount, // For API
      paymentNote: formData.paymentNote
    };

    console.log('New payment object:', newPayment);

    setFormData(prev => {
      const updatedPayments = [...prev.payments, newPayment];
      console.log('Updated payments array:', updatedPayments);
      console.log('Updated payments length:', updatedPayments.length);

      return {
        ...prev,
        payments: updatedPayments,
        // amount: '0.00', // Handled by useEffect
        paidOn: new Date().toLocaleString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        }),
        paymentMethod: 'Cash',
        paymentAccount: '', // Reset to empty/default
        paymentNote: ''
      };
    });

    console.log('=== PAYMENT ENTRY ADDED ===');
  };

  const handleRemovePayment = (paymentId) => {
    setFormData(prev => ({
      ...prev,
      payments: prev.payments.filter(payment => payment.id !== paymentId)
    }));
  };

  const calculateTotals = (data) => {
    console.log('calculateTotals called with data:', data);

    const productAmount = parseFloat(data.productAmount) || 0;
    let discountAmount = parseFloat(data.discountAmount) || 0;

    // Calculate discount based on type
    if (data.discountType === 'Percentage') {
      discountAmount = (productAmount * discountAmount) / 100;
    }

    const orderTaxAmount = parseFloat(data.orderTaxAmount) || 0;
    const shippingCharges = parseFloat(data.shippingCharges) || 0;
    const roundOff = parseFloat(data.roundOff) || 0;

    console.log('Parsed values:', { productAmount, discountAmount, discountType: data.discountType, orderTaxAmount, shippingCharges, roundOff });

    // Calculate additional expenses total
    const additionalExpensesTotal = data.additionalExpenses ?
      data.additionalExpenses.reduce((sum, expense) => sum + (parseFloat(expense.amount) || 0), 0) : 0;

    // Step 1: Calculate subtotal after discount
    const subtotal = productAmount - discountAmount;

    // Step 2: Add order tax
    const totalWithTax = subtotal + orderTaxAmount;

    // Step 3: Add shipping charges
    const totalWithShipping = totalWithTax + shippingCharges;

    // Step 4: Add additional expenses
    const totalWithExpenses = totalWithShipping + additionalExpensesTotal;

    // Step 5: Calculate automatic round-off (to nearest 0.05)
    const remainder = totalWithExpenses % 0.05;
    let autoRoundOff = 0;
    let finalTotal = totalWithExpenses;

    if (remainder > 0.025) {
      autoRoundOff = 0.05 - remainder;
      finalTotal = totalWithExpenses + autoRoundOff;
    } else if (remainder > 0) {
      autoRoundOff = -remainder;
      finalTotal = totalWithExpenses - remainder;
    }

    // Store calculation steps for display
    const calculationSteps = {
      productAmount: productAmount.toFixed(2),
      discountAmount: discountAmount.toFixed(2),
      orderTax: orderTaxAmount.toFixed(2),
      shippingCharges: shippingCharges.toFixed(2),
      additionalExpenses: additionalExpensesTotal.toFixed(2),
      totalBeforeRoundOff: totalWithExpenses.toFixed(2),
      roundOffAmount: autoRoundOff.toFixed(2),
      finalTotal: finalTotal.toFixed(2)
    };

    console.log('Setting calculation steps:', calculationSteps);

    setFormData(prev => ({
      ...prev,
      subtotal: subtotal.toFixed(2), // Store subtotal
      totalPayable: finalTotal.toFixed(2),
      roundOff: autoRoundOff.toFixed(2),
      calculationSteps: calculationSteps
    }));
  };

  const generateInvoiceHash = (saleId, invoiceNo) => {
    // Generate a unique hash similar to the format: cf5b05faa4708a783de848411edcc475
    const timestamp = Date.now().toString();
    const random = Math.random().toString(36).substring(2, 15);
    const combined = `${saleId}-${invoiceNo}-${timestamp}-${random}`;

    // Simple hash function to create a 32-character hex string
    let hash = 0;
    for (let i = 0; i < combined.length; i++) {
      const char = combined.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }

    // Convert to hex and pad to 32 characters
    const hexHash = Math.abs(hash).toString(16).padStart(8, '0');
    const fullHash = (hexHash + random + timestamp).substring(0, 32).padEnd(32, '0');

    return fullHash;
  };




  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log('=== SUBMIT STARTED ===');
    console.log('Form data:', formData);
    console.log('Current payments array at submit start:', formData.payments);
    console.log('Payments length at submit start:', formData.payments?.length || 0);
    console.log('Amount field value:', formData.amount);
    console.log('Payment method field value:', formData.paymentMethod);

    // Check if products are added FIRST (before other validations)
    if (!formData.products || formData.products.length === 0) {
      console.log('No products added');
      setSuccessMessage('No product added. Add some product');
      return;
    }

    if (!validateForm()) {
      console.log('Validation failed');
      setSuccessMessage('');
      return;
    }

    // Stock validation - check if quantity exceeds available stock
    if (formData.status.toLowerCase() === 'final') {
      const stockErrors = [];
      formData.products.forEach(product => {
        const qty = parseFloat(product.quantity) || 0;
        const available = parseFloat(product.stock_available) || 0;
        if (qty > available) {
          stockErrors.push(`${product.name}: requested ${qty}, available ${available}`);
        }
      });
      if (stockErrors.length > 0) {
        const errorMsg = `Insufficient stock:\n${stockErrors.join('\n')}`;
        console.log('Stock validation failed:', stockErrors);
        toast.error(errorMsg);
        setSuccessMessage(errorMsg);
        return;
      }
    }

    // Use existing payments only - do not auto-create
    let paymentsToUse = formData.payments || [];
    console.log('Using existing payments:', paymentsToUse);
    console.log('Payments length:', paymentsToUse.length);

    // Continue with submission even if no payments
    if (paymentsToUse.length === 0) {
      console.log('No payments added - submitting without payment');
    }

    console.log('Final payments to use:', paymentsToUse);
    console.log('Final payments length:', paymentsToUse.length);
    console.log('Payment details:', JSON.stringify(paymentsToUse, null, 2));

    console.log('Validation passed, preparing to save...');

    // Log formData.products to verify variation_id is preserved
    console.log('=== FORM DATA PRODUCTS BEFORE MAPPING ===');
    formData.products.forEach((product, index) => {
      console.log(`FormData Product ${index}:`, {
        id: product.id,
        variation_id: product.variation_id,
        name: product.name,
        quantity: product.quantity
      });
    });
    console.log('=== END FORM DATA PRODUCTS ===');

    setIsSubmitting(true);
    setSuccessMessage('');

    try {
      // Prepare data for API - matching the exact structure provided
      const sellData = {
        contact_id: parseInt(formData.customerId) || null,
        location_id: parseInt(formData.locationId),
        invoice_no: formData.invoiceNo || null,
        status: (formData.status === 'Final' || formData.status === 'final') ? 'final' : 'draft',
        sub_status: formData.status === 'Final' ? null :
                    formData.status === 'Quotation' ? 'quotation' :
                    formData.status === 'Proforma' ? 'proforma' : 'draft',
        is_quotation: formData.status === 'Quotation' ? 1 : 0,
        final_total: parseFloat(formData.totalPayable || 0),
        sale_date: dateUtils.formatUTCForApi(formData.saleDate || new Date()),
        transaction_date: dateUtils.formatUTCForApi(formData.saleDate || new Date()),
        tax_rate_id: (() => {
          const selectedTax = taxRates.find(t => t.name === formData.orderTax);
          return (selectedTax && selectedTax.id > 0) ? selectedTax.id : null;
        })(),
        tax_amount: parseFloat(formData.orderTaxAmount || 0),
        discount_type: formData.discountType === 'Percentage' ? 'percentage' : 'fixed',
        discount_amount: parseFloat(formData.discountAmount || 0),
        is_credit_sale: paymentsToUse.length === 0,
        is_suspend: false,
        sale_note: formData.sellNote || null,
        additional_notes: formData.additionalNotes || null,
        staff_note: formData.staffNote || null,
        commission_agent: formData.commissionAgent ? parseInt(formData.commissionAgent) : null,
        exchange_rate: 1,
        price_group: formData.priceGroup ? parseInt(formData.priceGroup) : null,
        types_of_service_id: formData.typesOfServiceId ? parseInt(formData.typesOfServiceId) : null,
        invoice_scheme_id: formData.invoiceScheme ? parseInt(formData.invoiceScheme) : null,
        res_table_id: formData.resTableId ? parseInt(formData.resTableId) : null,
        res_waiter_id: formData.resWaiterId ? parseInt(formData.resWaiterId) : null,
        shipping_status: formData.shippingStatus && formData.shippingStatus !== 'Please Select' ? formData.shippingStatus : 'ordered',
        is_recurring: false,
        recurring_interval: null,
        recurring_interval_count: null,
        subscription_repeat_after: null,
        shipping_details: formData.shippingDetails || null,
        shipping_charges: parseFloat(formData.shippingCharges || 0),
        deliveredTo: formData.deliveredTo || null,
        deliveryPerson: formData.deliveryPerson || null,

        // Service Custom Fields
        service_custom_field_1: formData.serviceCustomField1 || null,
        service_custom_field_2: formData.serviceCustomField2 || null,
        service_custom_field_3: formData.serviceCustomField3 || null,
        service_custom_field_4: formData.serviceCustomField4 || null,
        service_custom_field_5: formData.serviceCustomField5 || null,
        service_custom_field_6: formData.serviceCustomField6 || null,

        // Additional Expenses - Map from additionalExpenses array
        additional_expense_key_1: (formData.additionalExpenses && formData.additionalExpenses[0]?.name) || null,
        additional_expense_value_1: (formData.additionalExpenses && formData.additionalExpenses[0]?.amount && formData.additionalExpenses[0].amount !== '') ? parseFloat(formData.additionalExpenses[0].amount) : 0,
        additional_expense_key_2: (formData.additionalExpenses && formData.additionalExpenses[1]?.name) || null,
        additional_expense_value_2: (formData.additionalExpenses && formData.additionalExpenses[1]?.amount && formData.additionalExpenses[1].amount !== '') ? parseFloat(formData.additionalExpenses[1].amount) : 0,
        additional_expense_key_3: (formData.additionalExpenses && formData.additionalExpenses[2]?.name) || null,
        additional_expense_value_3: (formData.additionalExpenses && formData.additionalExpenses[2]?.amount && formData.additionalExpenses[2].amount !== '') ? parseFloat(formData.additionalExpenses[2].amount) : 0,
        additional_expense_key_4: (formData.additionalExpenses && formData.additionalExpenses[3]?.name) || null,
        additional_expense_value_4: (formData.additionalExpenses && formData.additionalExpenses[3]?.amount && formData.additionalExpenses[3].amount !== '') ? parseFloat(formData.additionalExpenses[3].amount) : 0,

        // Products Array
        products: formData.products.map(product => {
          console.log('[handleSubmit] Mapping product:', {
            id: product.id,
            variation_id: product.variation_id,
            name: product.name
          });

          // Ensure variation_id is always a valid number
          let variationId = product.variation_id;
          if (!variationId || isNaN(parseInt(variationId))) {
            variationId = product.variation_id || (product.product_variations?.[0]?.variations?.[0]?.id) || product.id;
          }
          variationId = parseInt(variationId);

          console.log('[handleSubmit] Final variation_id:', variationId);

          return {
            product_id: parseInt(product.id),
            variation_id: variationId,
            quantity: parseFloat(product.quantity),
            unit_price: parseFloat(product.unitPrice || product.sellPrice || 0),
            tax_id: (() => {
              const selectedTax = taxRates.find(t => t.name === product.tax);
              return (selectedTax && selectedTax.id > 0) ? selectedTax.id : null;
            })(),
            discount_type: (product.discountType || '').toLowerCase() === 'percentage' ? 'percentage' : 'fixed',
            discount_amount: parseFloat(product.discount || 0),
            line_order_status: product.lineOrderStatus || 'completed',
            lot_no: product.lotNo || null,
            expire_date: product.expireDate || null,
            woocommerce_line_item_id: null
          };
        }),

        // Payment Array
        payments: paymentsToUse && paymentsToUse.length > 0 ? paymentsToUse.map(payment => ({
          payment_amount: parseFloat(payment.amount),
          payment_method: (payment.paymentMethod || 'cash').toLowerCase(),
          payment_note: payment.paymentNote || null,
          account_id: payment.account_id ? parseInt(payment.account_id) : null,
          check_number: payment.checkNumber || null,
          paid_on: dateUtils.formatDateForApi(payment.paidOn ? new Date(payment.paidOn) : (formData.saleDate ? new Date(formData.saleDate) : new Date()))
        })) : []
      };

      // Add check image if applicable
      // Note: Full file upload requires FormData instead of JSON, but assuming API handles base64 or requires separate upload based on existing structure.
      // If FormData is required for file upload, the `apiService.saveSell` needs to handle it.
      // For now, attaching it to structured data assuming future multi-part or base64 implementation.

      console.log('=== REQUEST DATA TO BE SENT TO API ===');
      console.log('sellData:', JSON.stringify(sellData, null, 2));
      console.log('sellData (compact):', sellData);
      console.log('Products count:', sellData.products.length);
      console.log('Payments count:', sellData.payments.length);
      console.log('Payments array:', sellData.payments);
      console.log('Final total:', sellData.final_total);
      console.log('Contact ID:', sellData.contact_id);
      console.log('Location ID:', sellData.location_id);
      console.log('Status:', sellData.status);
      console.log('Is credit sale:', sellData.is_credit_sale);
      console.log('=== END REQUEST DATA ===');

      console.log('Prepared sellData:', sellData);
      console.log('Calling apiService.saveSell...');

      // Save or Update to API
      let apiResponse;
      if (isEditMode && editSale) {
        const saleId = editSale.apiData?.id || editSale.id;
        console.log('=== COMPLETE PAYLOAD BEFORE UPDATE API CALL ===');
        console.log('Sale ID to update:', saleId);
        console.log('Full sellData payload:');
        console.log(JSON.stringify(sellData, null, 2));
        console.log('Products array:');
        console.log(JSON.stringify(sellData.products, null, 2));
        console.log('Payments array:');
        console.log(JSON.stringify(sellData.payments, null, 2));
        console.log('=== END PAYLOAD LOGGING ===');
        apiResponse = await apiService.updateSell(saleId, sellData);
        console.log('Update API Response:', apiResponse);
        toast.success('Draft updated successfully!');
        setSuccessMessage('Sale updated successfully!');
      } else {
        apiResponse = await apiService.saveSell(sellData);
        console.log('API Response received:', apiResponse);
        toast.success('Draft saved successfully!');
        setSuccessMessage('Sale saved successfully!');
      }
      // Update Shipping Status if selected
      const finalSaleId = apiResponse.id || apiResponse.data?.id || apiResponse.transaction_id || apiResponse.data?.transaction_id;
      console.log('Extracted finalSaleId for shipping update:', finalSaleId);

      if (finalSaleId && formData.shippingStatus && formData.shippingStatus !== 'Please Select') {
        try {
          console.log('Calling updateShippingStatus for ID:', finalSaleId, 'Status:', formData.shippingStatus);
          await apiService.updateShippingStatus(finalSaleId, formData.shippingStatus);
          console.log('Shipping status updated successfully');
        } catch (shippingError) {
          console.error('Error updating shipping status:', shippingError);
          // We don't block the whole process if shipping update fails, but we log it
        }
      }

      // Send shipping_details separately with delivered_to and delivery_person
      if (finalSaleId) {
        try {
          const shippingDetailsData = {
            shipping_address: formData.shippingAddress || null,
            shipping_city: formData.shippingCity || null,
            shipping_state: formData.shippingState || null,
            shipping_country: formData.shippingCountry || null,
            shipping_zip_code: formData.shippingZipCode || null,
            shipping_charge: parseFloat(formData.shippingCharges || 0),
            delivered_to: formData.deliveredTo || null,
            delivery_person: formData.deliveryPerson || null
          };

          console.log('Sending shipping_details separately:', shippingDetailsData);
          await apiService.saveShippingDetails(finalSaleId, shippingDetailsData);
          console.log('Shipping details saved successfully');
        } catch (shippingDetailsError) {
          console.error('Error saving shipping details:', shippingDetailsError);
          // We don't block the whole process if shipping details save fails
        }
      }


      // Store sale data for WhatsApp notification
      const selectedCustomer = customers.find(c => c.id === parseInt(formData.customerId));

      // Extract sale ID from various possible locations in the response
      const saleId = apiResponse.data?.id ||
        apiResponse.id ||
        apiResponse.data?.transaction_id ||
        apiResponse.transaction_id ||
        apiResponse.data?.sale_id ||
        apiResponse.sale_id;

      // Try to get the actual invoice number from API response, otherwise generate one
      const saleInvoiceNo = apiResponse.data?.invoice_no ||
        apiResponse.invoice_no ||
        apiResponse.invoice_no ||
        `INV-${Date.now()}`;

      // Fetch sale details to get the actual invoice URL and invoice number
      let actualInvoiceUrl = null;
      let actualInvoiceNo = saleInvoiceNo;

      try {
        // First try to get from sales list API (which might have more complete data)
        const salesList = await apiService.getSales({ per_page: 1, start_date: formData.saleDate, end_date: formData.saleDate });

        // Find our sale in the list - handle different response structures
        let ourSale = null;
        if (Array.isArray(salesList)) {
          ourSale = salesList.find(s => s.id === saleId || s.transaction_id === saleId);
        } else if (salesList.data && Array.isArray(salesList.data)) {
          ourSale = salesList.data.find(s => s.id === saleId || s.transaction_id === saleId);
        }

        if (ourSale) {
          // Check for invoice URL in the sale from list
          const possibleUrlFields = ['invoice_url', 'invoiceLink', 'invoice_link', 'url', 'link'];
          for (const field of possibleUrlFields) {
            if (ourSale[field]) {
              actualInvoiceUrl = ourSale[field];
              break;
            }
          }

          // Check for invoice number
          const possibleInvoiceFields = ['invoice_no', 'invoiceNo', 'invoice_number', 'invoice_number'];
          for (const field of possibleInvoiceFields) {
            if (ourSale[field]) {
              actualInvoiceNo = ourSale[field];
              break;
            }
          }
        }

        // If still not found, try single sale details
        if (!actualInvoiceUrl) {
          const saleDetails = await apiService.getSale(saleId);

          // The response might be wrapped in a data array
          let actualSaleDetails = saleDetails;
          if (saleDetails.data && Array.isArray(saleDetails.data) && saleDetails.data.length > 0) {
            actualSaleDetails = saleDetails.data[0];
          }

          // Check for invoice URL in single sale details
          const possibleUrlFields = ['invoice_url', 'invoiceLink', 'invoice_link', 'url', 'link'];
          for (const field of possibleUrlFields) {
            if (actualSaleDetails?.[field]) {
              actualInvoiceUrl = actualSaleDetails[field];
              break;
            }
          }

          // Check for invoice number
          const possibleInvoiceFields = ['invoice_no', 'invoiceNo', 'invoice_number', 'invoice_number'];
          for (const field of possibleInvoiceFields) {
            if (actualSaleDetails?.[field]) {
              actualInvoiceNo = actualSaleDetails[field];
              break;
            }
          }
        }

      } catch (error) {
        // Fall back to generated hash if fetch fails
        const invoiceHash = generateInvoiceHash(saleId, saleInvoiceNo);
        actualInvoiceUrl = `https://et-pos.store/invoice/${invoiceHash}`;
      }

      // Use production URL if we still don't have the actual invoice URL
      if (!actualInvoiceUrl) {
        const invoiceHash = generateInvoiceHash(saleId, actualInvoiceNo);
        actualInvoiceUrl = `https://et-pos.store/invoice/${invoiceHash}`;
      }

      const saleDataForNotification = {
        id: saleId,
        invoiceNumber: actualInvoiceNo,
        date: formData.saleDate,
        total: formData.totalPayable,
        paymentStatus: apiResponse.data?.payment_status || apiResponse.payment_status || 'Due',
        invoiceUrl: actualInvoiceUrl,
        businessName: "Lefty's Liquor",
        items: formData.products.length
      };

      setLastSaleData(saleDataForNotification);

      // Show WhatsApp notification option if customer has phone
      if (selectedCustomer?.phone) {
        setTimeout(() => {
          setShowWhatsAppModal(true);
        }, 1000);
      } else {
        // No phone — reset form immediately (modal reset handles the other case)
        setFormData({
          ...initialFormData,
          saleDate: new Date().toISOString().split('T')[0],
          paidOn: new Date().toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }),
        });
        setErrors({});
        setCustomerSearchTerm('');
        setTimeout(() => setSuccessMessage(''), 3000);
      }

    } catch (error) {
      console.error('=== ERROR SAVING SALE ===');
      console.error('Error type:', error.constructor.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      console.error('Full error:', error);
      setSuccessMessage('Error saving sale. Please try again.');
      toast.error(`Error: ${error.message}`);
    }

    setIsSubmitting(false);
    console.log('=== SUBMIT COMPLETED ===');
  };

  const handleSaveAndPrint = async (e) => {
    e.preventDefault();

    // Check if products are added FIRST (before other validations)
    if (!formData.products || formData.products.length === 0) {
      setSuccessMessage('No product added. Add some product');
      return;
    }

    if (!validateForm()) {
      setSuccessMessage('');
      return;
    }

    // Stock validation - check if quantity exceeds available stock
    if (formData.status.toLowerCase() === 'final') {
      const stockErrors = [];
      formData.products.forEach(product => {
        const qty = parseFloat(product.quantity) || 0;
        const available = parseFloat(product.stock_available) || 0;
        if (qty > available) {
          stockErrors.push(`${product.name}: requested ${qty}, available ${available}`);
        }
      });
      if (stockErrors.length > 0) {
        const errorMsg = `Insufficient stock:\n${stockErrors.join('\n')}`;
        toast.error(errorMsg);
        setSuccessMessage(errorMsg);
        return;
      }
    }

    // Use existing payments only - do not auto-create for save & print
    let paymentsToUse = formData.payments || [];
    console.log('Using existing payments for save & print:', paymentsToUse);
    console.log('Payments length for save & print:', paymentsToUse.length);

    // Continue with save & print even if no payments
    if (paymentsToUse.length === 0) {
      console.log('No payments added - saving & printing without payment');
    }

    console.log('Final payments to use for save & print:', paymentsToUse);
    console.log('Final payments length for save & print:', paymentsToUse.length);

    setIsSubmitting(true);
    setSuccessMessage('');

    try {
      // Use the same data structure as handleSubmit
      const sellData = {
        contact_id: parseInt(formData.customerId) || null,
        location_id: parseInt(formData.locationId) || null,
        invoice_no: formData.invoiceNo || null,
        status: (formData.status === 'Final' || formData.status === 'final') ? 'final' : 'draft',
        sub_status: formData.status === 'Final' ? null :
                    formData.status === 'Quotation' ? 'quotation' :
                    formData.status === 'Proforma' ? 'proforma' : 'draft',
        is_quotation: formData.status === 'Quotation' ? 1 : 0,
        final_total: parseFloat(formData.totalPayable || 0),
        sale_date: dateUtils.formatDateForApi(formData.saleDate || new Date()),
        tax_rate_id: (() => {
          const selectedTax = taxRates.find(t => t.name === formData.orderTax);
          return (selectedTax && selectedTax.id > 0) ? selectedTax.id : null;
        })(),
        tax_amount: parseFloat(formData.orderTaxAmount || 0),
        discount_type: formData.discountType === 'Percentage' ? 'percentage' : 'fixed',
        discount_amount: parseFloat(formData.discountAmount || 0),
        is_credit_sale: (formData.payments || []).length === 0,
        is_suspend: false,
        sale_note: formData.sellNote || null,
        additional_notes: formData.additionalNotes || null,
        staff_note: formData.staffNote || null,
        commission_agent: formData.commissionAgent ? parseInt(formData.commissionAgent) : null,
        exchange_rate: 1,
        price_group: formData.priceGroup ? parseInt(formData.priceGroup) : null,
        types_of_service_id: formData.typesOfServiceId ? parseInt(formData.typesOfServiceId) : null,
        invoice_scheme_id: formData.invoiceScheme ? parseInt(formData.invoiceScheme) : null,
        res_table_id: formData.resTableId ? parseInt(formData.resTableId) : null,
        res_waiter_id: formData.resWaiterId ? parseInt(formData.resWaiterId) : null,
        shipping_status: formData.shippingStatus && formData.shippingStatus !== 'Please Select' ? formData.shippingStatus : 'ordered',
        is_recurring: false,
        recurring_interval: null,
        recurring_interval_count: null,
        subscription_repeat_after: null,
        shipping_details: formData.shippingDetails || null,
        shipping_charges: parseFloat(formData.shippingCharges || 0),
        deliveredTo: formData.deliveredTo || null,
        deliveryPerson: formData.deliveryPerson || null,

        service_custom_field_1: formData.serviceCustomField1 || null,
        service_custom_field_2: formData.serviceCustomField2 || null,
        service_custom_field_3: formData.serviceCustomField3 || null,
        service_custom_field_4: formData.serviceCustomField4 || null,
        service_custom_field_5: formData.serviceCustomField5 || null,
        service_custom_field_6: formData.serviceCustomField6 || null,

        // Additional Expenses - Map from additionalExpenses array
        additional_expense_key_1: (formData.additionalExpenses && formData.additionalExpenses[0]?.name) || null,
        additional_expense_value_1: (formData.additionalExpenses && formData.additionalExpenses[0]?.amount && formData.additionalExpenses[0].amount !== '') ? parseFloat(formData.additionalExpenses[0].amount) : 0,
        additional_expense_key_2: (formData.additionalExpenses && formData.additionalExpenses[1]?.name) || null,
        additional_expense_value_2: (formData.additionalExpenses && formData.additionalExpenses[1]?.amount && formData.additionalExpenses[1].amount !== '') ? parseFloat(formData.additionalExpenses[1].amount) : 0,
        additional_expense_key_3: (formData.additionalExpenses && formData.additionalExpenses[2]?.name) || null,
        additional_expense_value_3: (formData.additionalExpenses && formData.additionalExpenses[2]?.amount && formData.additionalExpenses[2].amount !== '') ? parseFloat(formData.additionalExpenses[2].amount) : 0,
        additional_expense_key_4: (formData.additionalExpenses && formData.additionalExpenses[3]?.name) || null,
        additional_expense_value_4: (formData.additionalExpenses && formData.additionalExpenses[3]?.amount && formData.additionalExpenses[3].amount !== '') ? parseFloat(formData.additionalExpenses[3].amount) : 0,

        products: formData.products.map(product => {
          console.log('[handleSaveAndPrint] Mapping product:', {
            id: product.id,
            variation_id: product.variation_id,
            name: product.name
          });

          // Ensure variation_id is always a valid number
          let variationId = product.variation_id;
          if (!variationId || isNaN(parseInt(variationId))) {
            variationId = product.variation_id || (product.product_variations?.[0]?.variations?.[0]?.id) || product.id;
          }
          variationId = parseInt(variationId);

          console.log('[handleSaveAndPrint] Final variation_id:', variationId);

          return {
            product_id: parseInt(product.id),
            variation_id: variationId,
            quantity: parseFloat(product.quantity),
            unit_price: parseFloat(product.unitPrice || product.sellPrice || 0),
            tax_id: (() => {
              const selectedTax = taxRates.find(t => t.name === product.tax);
              return (selectedTax && selectedTax.id > 0) ? selectedTax.id : null;
            })(),
            discount_type: (product.discountType || '').toLowerCase() === 'percentage' ? 'percentage' : 'fixed',
            discount_amount: parseFloat(product.discount || 0),
            line_order_status: product.lineOrderStatus || 'completed',
            lot_no: product.lotNo || null,
            expire_date: product.expireDate || null,
            woocommerce_line_item_id: null
          };
        }),

        payments: paymentsToUse && paymentsToUse.length > 0 ? paymentsToUse.map(payment => ({
          payment_amount: parseFloat(payment.amount),
          payment_method: (payment.paymentMethod || 'cash').toLowerCase(),
          payment_note: payment.paymentNote || null,
          account_id: payment.account_id ? parseInt(payment.account_id) : null,
          check_number: payment.checkNumber || null,
          paid_on: dateUtils.formatDateForApi(payment.paidOn || formData.saleDate || new Date())
        })) : []
      };

      console.log('=== SAVE & PRINT REQUEST DATA TO BE SENT TO API ===');
      console.log('sellData:', JSON.stringify(sellData, null, 2));
      console.log('sellData (compact):', sellData);
      console.log('Products count:', sellData.products.length);
      console.log('Payments count:', sellData.payments.length);
      console.log('Payments array:', sellData.payments);
      console.log('Final total:', sellData.final_total);
      console.log('Contact ID:', sellData.contact_id);
      console.log('Location ID:', sellData.location_id);
      console.log('Status:', sellData.status);
      console.log('Is credit sale:', sellData.is_credit_sale);
      console.log('=== END SAVE & PRINT REQUEST DATA ===');

      // Save or Update to API
      let apiResponse;
      let finalSaleId;
      if (isEditMode && editSale) {
        const saleId = editSale.apiData?.id || editSale.id;
        console.log('Updating existing sale ID (Save & Print):', saleId);
        apiResponse = await apiService.updateSell(saleId, sellData);
        console.log('Update API Response (Save & Print):', apiResponse);
        toast.success('Draft updated successfully!');
        finalSaleId = saleId;
      } else {
        apiResponse = await apiService.saveSell(sellData);
        toast.success('Draft saved successfully!');
        console.log('Sale saved to API for print:', apiResponse);
        finalSaleId = apiResponse.id || apiResponse.data?.id || apiResponse.transaction_id || apiResponse.data?.transaction_id;
      }
      console.log('Extracted finalSaleId (Save & Print) for shipping update:', finalSaleId);

      if (finalSaleId && formData.shippingStatus && formData.shippingStatus !== 'Please Select') {
        try {
          console.log('Calling updateShippingStatus (Save & Print) for ID:', finalSaleId, 'Status:', formData.shippingStatus);
          await apiService.updateShippingStatus(finalSaleId, formData.shippingStatus);
          console.log('Shipping status updated successfully');
        } catch (shippingError) {
          console.error('Error updating shipping status for print:', shippingError);
        }
      }

      // Trigger PDF Invoice print
      setTimeout(() => {
        const totalPaid = (formData.payments || []).reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
        const invoiceData = {
          invoiceNumber: apiResponse.invoice_no || apiResponse.data?.invoice_no || `DFT-${Date.now()}`,
          invoiceDate: formData.saleDate ? new Date(formData.saleDate).toLocaleDateString() : new Date().toLocaleDateString(),
          customerName: formData.customer || 'Walk-in Customer',
          customerPhone: customers.find(c => c.id === parseInt(formData.customerId))?.phone || '',
          customerAddress: formData.billingAddress || '',
          shippingAddress: formData.shippingAddress || '',
          items: formData.products.map(p => ({
            sku: p.sku || p.barcode || '',
            name: p.name,
            quantity: parseFloat(p.quantity),
            unitPrice: parseFloat(p.unitPrice || p.sellPrice || 0),
            total: (parseFloat(p.quantity) * parseFloat(p.unitPrice || p.sellPrice || 0)) - parseFloat(p.discount || 0)
          })),
          payments: (formData.payments || []).map(p => ({
            method: p.paymentMethod,
            amount: p.amount,
            date: p.paidOn ? new Date(p.paidOn).toLocaleDateString() : new Date().toLocaleDateString(),
            cashier: 'encore',
            taxId: 'PAN12345K'
          })),
          subtotal: parseFloat(formData.subtotal || 0),
          tax: parseFloat(formData.orderTaxAmount || 0),
          discount: parseFloat(formData.discountAmount || 0),
          total: parseFloat(formData.totalPayable || 0),
          totalPaid: totalPaid,
          amountDue: Math.max(0, parseFloat(formData.totalPayable || 0) - totalPaid),
          businessAddress: '14199 E HIGHWAY 12 ROGERS,, Little Rock, AR, 72756-8, USA'
        };
        invoiceGenerator.printInvoicePDF(invoiceData);
        setSuccessMessage('');
      }, 500);

    } catch (error) {
      console.error('Error saving sale for print:', error);
      setSuccessMessage(`Error saving sale: ${error.message || 'Please try again.'}`);
    }

    setIsSubmitting(false);
  };

  return (
    <div className="sell-form-container add-sell-override">
      {/* Header */}
      <div className="page-header">
        <button
          onClick={() => navigate('/sell')}
          className="back-button"
          style={{
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            padding: '0.5rem 0.75rem',
            cursor: 'pointer',
            fontSize: '1.2rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '40px',
            height: '40px'
          }}
          title="Back to Sell Lists"
        >
          ←
        </button>
        <h2 style={{ fontSize: '1.25rem' }}>{isEditMode ? 'Edit Draft' : 'Add Draft'}</h2>
      </div>

      <form onSubmit={handleSubmit} className="sell-form">
        {/* Success/Error Messages */}

        {/* Success/Error Messages */}
        {successMessage && (
          <div className={`message ${successMessage.includes('Error') ? 'error-message' : 'success-message'}`}>
            {successMessage}
          </div>
        )}

        {/* Customer Information Section */}
        <div className="form-section-content">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="customer">Customer:*</label>
              <div className="customer-dropdown-container">
                <div className="input-with-icon-and-button">
                  <div className="input-icon-box">
                    <User size={18} />
                  </div>
                  <input
                    type="text"
                    id="customer"
                    name="customer"
                    value={customerSearchTerm}
                    onChange={(e) => handleCustomerSearch(e.target.value)}
                    onFocus={handleCustomerInputFocus}
                    onBlur={handleCustomerInputBlur}
                    className={`form-input-clean ${errors.customer ? 'error' : ''}`}
                    placeholder="Search customer..."
                    autoComplete="off"
                  />
                </div>
                {showCustomerDropdown && (
                  <div className="customer-dropdown">
                    {isLoadingCustomers ? (
                      <div className="customer-dropdown-item loading">Loading...</div>
                    ) : customerError ? (
                      <div className="customer-dropdown-item error">{customerError}</div>
                    ) : filteredCustomers.length > 0 ? (
                      filteredCustomers.map((customer) => (
                        <div
                          key={customer.id}
                          className="customer-dropdown-item"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleCustomerSelect(customer);
                          }}
                        >
                          <div className="customer-name">{customer.name}</div>
                        </div>
                      ))
                    ) : (
                      <div className="customer-dropdown-item no-results">No results</div>
                    )}
                  </div>
                )}
              </div>
              {errors.customer && <span className="error-text">{errors.customer}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="billingAddress">Billing Address:</label>
              <input
                type="text"
                id="billingAddress"
                name="billingAddress"
                value={formData.billingAddress}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label htmlFor="shippingAddress">Shipping Address:</label>
              <input
                type="text"
                id="shippingAddress"
                name="shippingAddress"
                value={formData.shippingAddress}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label htmlFor="payTerm">Pay term:</label>
              <div className="payterm-input-group">
                <input
                  type="number"
                  id="payTerm"
                  name="payTerm"
                  value={formData.payTerm}
                  onChange={handleInputChange}
                  className="form-input payterm-input"
                  min="0"
                />
                <select
                  id="payTermPeriod"
                  name="payTermPeriod"
                  value={formData.payTermPeriod || 'Months'}
                  onChange={handleInputChange}
                  className="form-select payterm-select"
                >
                  <option value="Months">Mon</option>
                  <option value="Days">Day</option>
                  <option value="Years">Yr</option>
                </select>
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="commissionAgent">Comm. Agent:</label>
              <select
                id="commissionAgent"
                name="commissionAgent"
                value={formData.commissionAgent}
                onChange={handleInputChange}
                className="form-select"
              >
                <option value="">Select</option>
                <option value="agent1">Agent 1</option>
                <option value="agent2">Agent 2</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="serviceStaff">Service staff</label>
              <select
                id="serviceStaff"
                name="serviceStaff"
                value={formData.serviceStaff}
                onChange={handleInputChange}
                className="form-select"
              >
                <option value="">Select</option>
                <option value="staff1">Staff 1</option>
                <option value="staff2">Staff 2</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="saleDate">Sale Date:*</label>
              <input
                type="date"
                id="saleDate"
                name="saleDate"
                value={formData.saleDate}
                onChange={handleInputChange}
                className={`form-input ${errors.saleDate ? 'error' : ''}`}
              />
            </div>

            <div className="form-group">
              <label htmlFor="status">Status:*</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className={`form-select ${errors.status ? 'error' : ''}`}
              >
                <option value="">Select</option>
                <option value="Final">Final</option>
                <option value="Draft">Draft</option>
                <option value="Quotation">Quote</option>
                <option value="Proforma">Proforma</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="locationId">Business Location:*</label>
              <select
                id="locationId"
                name="locationId"
                value={formData.locationId}
                onChange={handleInputChange}
                className={`form-select ${errors.locationId ? 'error' : ''}`}
              >
                {businessLocations.length === 0 ? (
                  <option value="">Loading...</option>
                ) : (
                  <>
                    <option value="">Select Location</option>
                    {businessLocations.map(loc => (
                      <option key={loc.id} value={String(loc.id)}>
                        {loc.name || loc.location_name || `Location ${loc.id}`}
                      </option>
                    ))}
                  </>
                )}
              </select>
              {errors.locationId && <span className="error-text">{errors.locationId}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="invoiceScheme">Invoice scheme:</label>
              <select
                id="invoiceScheme"
                name="invoiceScheme"
                value={formData.invoiceScheme}
                onChange={handleInputChange}
                className="form-select"
              >
                {isLoadingInvoiceSchemes ? (
                  <option value="">Loading...</option>
                ) : (
                  <>
                    <option value="">Select</option>
                    {invoiceSchemes.map(scheme => (
                      <option key={scheme.id} value={scheme.id}>
                        {scheme.name}
                      </option>
                    ))}
                  </>
                )}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="invoiceNo">Invoice No.:</label>
              <input
                type="text"
                id="invoiceNo"
                name="invoiceNo"
                value={formData.invoiceNo}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Auto"
              />
            </div>


          </div>

        </div>

        <hr className="section-divider" />

        {/* Product Section */}
        <div className="form-section-content">
          <h3>Product Information</h3>
          <div className="form-row">
            <div className="form-group full-width">
              <label htmlFor="productName">Enter Product name / SKU / Scan bar code</label>
              <div className="product-dropdown-container" style={{ position: 'relative' }}>
                  <input
                  type="text"
                  id="productName"
                  name="productName"
                  value={productSearchTerm}
                  onChange={(e) => handleProductSearch(e.target.value)}
                  onFocus={handleProductInputFocus}
                  onBlur={handleProductInputBlur}
                  className="form-input"
                  placeholder="Search products by name or SKU..."
                  autoComplete="off"
                  style={{ paddingRight: '10px' }}
                />
                {isLoadingProducts && (
                  <div className="product-loading">Loading products...</div>
                )}
                {showProductDropdown && !isLoadingProducts && (
                  <div className="product-dropdown">
                    {productError ? (
                      <div className="product-dropdown-item error">
                        {productError}
                      </div>
                    ) : filteredProducts.length > 0 ? (
                      <>
                        {filteredProducts.slice(0, displayLimit).map((product, index) => (
                          <div
                            key={product.id}
                            className="product-dropdown-item"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleProductSelect(product);
                            }}
                          >
                            <div className="product-name">
                              {product.product || product.name || product.product_name || product.title || product.description || 'Unknown Product'}
                              {(product.sku || product.code) && <span className="product-sku-inline"> • SKU: {product.sku || product.code}</span>}
                            </div>
                            <div className="product-details">
                              {(product.unit_price || product.price || product.cost) && <span className="product-price">${product.unit_price || product.price || product.cost}</span>}
                              {product.barcode && <span className="product-barcode">Barcode: {product.barcode}</span>}
                            </div>
                          </div>
                        ))}
                        {filteredProducts.length > displayLimit && (
                          <div
                            className="product-dropdown-item show-more"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setDisplayLimit(prev => prev + 50);
                            }}
                          >
                            Show {Math.min(50, filteredProducts.length - displayLimit)} more products...
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="product-dropdown-item no-results">
                        No products found
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="product-table">
            <table>
              <thead>
                <tr>
                  <th style={{ width: '30%' }}>Product</th>
                  <th style={{ width: '15%' }}>Quantity</th>
                  <th style={{ width: '12%' }}>Unit Price</th>
                  <th style={{ width: '12%' }}>Discount</th>
                  <th style={{ width: '10%' }}>Tax</th>
                  <th style={{ width: '10%' }}>Price inc. tax</th>
                  <th style={{ width: '10%' }}>Subtotal</th>
                  <th style={{ width: '5%' }}></th>
                </tr>
              </thead>
              <tbody>
                {formData.products.length > 0 ? (
                  formData.products.map((product) => {
                    console.log('Rendering product:', product);
                    return (
                      <tr key={product.id}>
                        <td>
                          <div className="product-info-cell">
                            <div className="product-name">{product.name}</div>
                            {product.sku && <div className="product-sku">{product.sku}</div>}
                            <textarea
                              className="product-description-input"
                              placeholder="Add product IMEI, Serial number or other informations here."
                            ></textarea>
                          </div>
                        </td>
                        <td>
                          <div className="quantity-group">
                            <div className="quantity-input-group">
                              <button type="button" className="qty-btn minus" onClick={() => updateProductQuantity(product.id, Math.max(1, (parseInt(product.quantity) || 0) - 1))}>-</button>
                              <input
                                type="number"
                                value={product.quantity}
                                onChange={(e) => updateProductQuantity(product.id, e.target.value)}
                                className="quantity-input-centered"
                                min="1"
                              />
                              <button type="button" className="qty-btn plus" onClick={() => updateProductQuantity(product.id, (parseInt(product.quantity) || 0) + 1)}>+</button>
                            </div>
                            <select className="unit-select">
                              <option value="Single">Single</option>
                            </select>
                          </div>
                        </td>
                        <td>
                          <input
                            type="number"
                            className="form-input unit-price-input"
                            value={product.unitPrice}
                            readOnly
                          />
                          <div className="previous-price-text">Previous unit price: <br />${(parseFloat(product.purchasePrice) || 0).toFixed(2)}</div>
                        </td>
                        <td>
                          <div className="discount-group">
                            <input
                              type="number"
                              value={product.discount}
                              onChange={(e) => updateProductDiscount(product.id, e.target.value)}
                              className="discount-input"
                              min="0"
                              step="0.01"
                              placeholder={product.discountType === 'Percentage' ? '%' : '$'}
                            />
                            <div className="discount-type-toggle">
                              <button
                                type="button"
                                className={`discount-type-btn ${product.discountType === 'Percentage' ? 'active' : ''}`}
                                onClick={() => updateProductDiscountType(product.id, 'Percentage')}
                              >
                                %
                              </button>
                              <button
                                type="button"
                                className={`discount-type-btn ${product.discountType === 'Fixed' ? 'active' : ''}`}
                                onClick={() => updateProductDiscountType(product.id, 'Fixed')}
                              >
                                $
                              </button>
                            </div>
                            <div className="previous-price-text">Previous discount: <br />{(() => {
                              const priceIncTax = parseFloat(product.priceIncTax) || 0;
                              let discountAmount = parseFloat(product.discount) || 0;
                              if (product.discountType === 'Percentage') {
                                discountAmount = (priceIncTax * discountAmount) / 100;
                              }
                              return `$${discountAmount.toFixed(2)}`;
                            })()}</div>
                          </div>
                        </td>
                        <td>
                          <select
                            value={product.tax}
                            onChange={(e) => updateProductTax(product.id, e.target.value)}
                            className="tax-select"
                          >
                            {taxRates.map((tax) => (
                              <option key={tax.id} value={tax.name}>
                                {tax.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="product-bold-text" style={{ fontWeight: 'bold' }}>${(parseFloat(product.priceIncTax) || 0).toFixed(2)}</td>
                        <td className="product-bold-text" style={{ fontWeight: 'bold' }}>${(parseFloat(product.subtotal) || 0).toFixed(2)}</td>
                        <td>
                          <button
                            type="button"
                            className="remove-product-btn"
                            onClick={() => removeProduct(product.id)}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="8" className="no-products">No products added</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="product-summary">
            <span>Items: {formData.products.length}</span>
            <span>Total: ${formData.products.reduce((sum, product) => sum + (parseFloat(product.subtotal) || 0), 0).toFixed(2)}</span>
          </div>
        </div>

        <hr className="section-divider" />



        {/* Shipping Section */}
        <div className="form-section-content">
          <h3>Shipping Details</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="shippingDetails">Shipping Details</label>
              <textarea
                id="shippingDetails"
                name="shippingDetails"
                value={formData.shippingDetails}
                onChange={handleInputChange}
                className="form-textarea"
                rows="1"
                style={{ minHeight: '64px' }}
              />
            </div>
            <div className="form-group">
              <label htmlFor="shippingAddress">Shipping Address</label>
              <textarea
                id="shippingAddress"
                name="shippingAddress"
                value={formData.shippingAddress}
                onChange={handleInputChange}
                className="form-textarea"
                rows="1"
                style={{ minHeight: '64px' }}
              />
            </div>
            <div className="form-group">
              <label htmlFor="shippingCharges">Shipping Charges</label>
              <input
                type="number"
                id="shippingCharges"
                name="shippingCharges"
                value={formData.shippingCharges}
                onChange={handleInputChange}
                className="form-input"
                step="0.01"
                min="0"
              />
            </div>
            <div className="form-group">
              <label htmlFor="shippingStatus">Shipping Status</label>
              <select
                id="shippingStatus"
                name="shippingStatus"
                value={formData.shippingStatus}
                onChange={handleInputChange}
                className="form-select"
              >
                <option value="">Please Select</option>
                <option value="ordered">Ordered</option>
                <option value="packed">Packed</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="deliveredTo">Delivered To:</label>
              <input
                type="text"
                id="deliveredTo"
                name="deliveredTo"
                value={formData.deliveredTo}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label htmlFor="deliveryPerson">Delivery Person:</label>
              <input
                type="text"
                id="deliveryPerson"
                name="deliveryPerson"
                value={formData.deliveryPerson}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>
            <div className="form-group shipping-document-group">
              <label>Shipping Documents:</label>
              <div className="shipping-doc-input-wrapper">
                <input
                  type="file"
                  className="form-input"
                  accept=".pdf,.csv,.zip,.doc,.docx,.jpeg,.jpg,.png"
                />
                <span className="file-hint">Max 5MB (PDF, JPG, PNG...)</span>
              </div>
            </div>
          </div>
        </div>

        <hr className="section-divider" />

        {/* Summary Output */}
        <div className="form-section-content">
          <div className="payment-summary" style={{ textAlign: 'right', fontWeight: 'bold', fontSize: '1.2rem', padding: '1rem' }}>
            <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.25rem' }}>(Round Off: 0)</div>
            <div>Total Payable: ${formData.totalPayable}</div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="form-section-content action-buttons-section">
          <div className="action-buttons">
            <button
              type="button"
              className="save-btn"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? '⏳ Saving...' : '💾 Save'}
            </button>
            <button
              type="button"
              className="save-print-btn"
              onClick={handleSaveAndPrint}
              disabled={isSubmitting}
            >
              {isSubmitting ? '⏳ Processing...' : '🖨️ Save & Print'}
            </button>
          </div>
        </div>
      </form >

      {/* WhatsApp Notification Modal */}
      {showWhatsAppModal && lastSaleData && (
        <WhatsAppIntegration
          saleData={lastSaleData}
          customerData={customers.find(c => c.id === parseInt(formData.customerId)) || {}}
          onClose={() => {
            setShowWhatsAppModal(false);
            setLastSaleData(null);
            setFormData({
              ...initialFormData,
              saleDate: dateUtils.getChicagoISOString().split('T')[0],
              paidOn: dateUtils.formatDateTime(new Date()),
            });
            setErrors({});
            setSuccessMessage('');
            setCustomerSearchTerm('');
            // Redirect to drafts list page with reload signal
            navigate('/sell/drafts', { state: { reloadSales: Date.now() } });
          }}
        />
      )}

      {/* Add Customer Modal */}
      {showAddCustomerModal && (
        <div className="modal-overlay" onClick={() => setShowAddCustomerModal(false)} style={{ zIndex: 9999 }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '95%', width: '1200px', maxHeight: '95vh', overflowY: 'auto', padding: 0 }}>
            <div className="modal-header" style={{ padding: '15px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0 }}>Add New Customer</h2>
              <button className="close-button" onClick={() => {
                setShowAddCustomerModal(false);
                loadCustomers(); // Reload customers after closing
              }} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>&times;</button>
            </div>
            <div style={{ padding: '20px' }}>
              <AddSupplier onSuccess={() => {
                setShowAddCustomerModal(false);
                loadCustomers();
              }} />
            </div>
          </div>
        </div>
      )}

      {/* Add Product Modal */}
      {showAddProductModal && (
        <div className="modal-overlay" onClick={() => setShowAddProductModal(false)} style={{ zIndex: 9999 }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '95%', width: '1200px', maxHeight: '95vh', overflowY: 'auto', padding: 0 }}>
            <div className="modal-header" style={{ padding: '15px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0 }}>Add New Product</h2>
              <button className="close-button" onClick={() => setShowAddProductModal(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>&times;</button>
            </div>
            <div style={{ padding: '20px' }}>
              <AddProduct onSuccess={() => {
                setShowAddProductModal(false);
                loadProducts();
              }} />
            </div>
          </div>
        </div>
      )}
    </div >
  );
};

export default AddDraft;
